/**
 * A stored body, rendered read-only.
 *
 * ITS OWN RENDERER, NOT A SECOND EDITOR. Mounting a TipTap instance to show an old version would
 * put two editors on one page, each with its own Y.Doc, and the Collaboration extension would have
 * to be left out of one of them -- which is a different document model rendering the same JSON.
 * That difference is exactly where "the preview looks nothing like the document" comes from.
 *
 * It walks the same ProseMirror JSON the editor stores and emits the same tags the editor's own
 * schema emits, so DocumentPaper's CSS styles it identically: the preview is the page, with the
 * typing turned off.
 *
 * ESCAPED, because this returns HTML that is injected. The text is somebody's document and may
 * contain anything; a procedure that mentions <script> is a procedure, not an attack, and it has to
 * render as the words it is.
 */
import { fieldText } from "./PageElements";

type Node = { type?: string; text?: string; marks?: any[]; attrs?: any; content?: Node[] };

function escape(text: string): string {
    return text.replace(/[&<>"']/g, ch => (
        { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch] as string));
}

/** The marks the editor can apply, wrapped from the inside out so nesting matches the editor's. */
function withMarks(html: string, marks: any[] = []): string {
    return marks.reduce((inner, mark) => {
        switch (mark.type) {
            case "bold": return `<strong>${inner}</strong>`;
            case "italic": return `<em>${inner}</em>`;
            case "underline": return `<u>${inner}</u>`;
            case "strike": return `<s>${inner}</s>`;
            case "code": return `<code>${inner}</code>`;
            case "link": return `<a href="${escape(mark.attrs?.href ?? "")}">${inner}</a>`;
            case "highlight":
                return `<mark style="background:${escape(mark.attrs?.color ?? "#FFF59D")}">${inner}</mark>`;
            case "textStyle": {
                const style = [
                    mark.attrs?.color ? `color:${escape(mark.attrs.color)}` : "",
                    mark.attrs?.fontFamily ? `font-family:${escape(mark.attrs.fontFamily)}` : "",
                    mark.attrs?.fontSize ? `font-size:${escape(mark.attrs.fontSize)}` : "",
                ].filter(Boolean).join(";");
                return style ? `<span style="${style}">${inner}</span>` : inner;
            }
            // SUGGESTIONS RENDER AS SUGGESTIONS IN A PREVIEW TOO. A version that was saved mid
            // review had tracked changes in it, and showing that text as though it were settled
            // would misrepresent the moment being looked at.
            case "insertion":
                return `<ins class="suggestion suggestion-insertion" style="--author:${
                    escape(mark.attrs?.colour ?? "#5ABCED")}">${inner}</ins>`;
            case "deletion":
                return `<del class="suggestion suggestion-deletion" style="--author:${
                    escape(mark.attrs?.colour ?? "#C0392B")}">${inner}</del>`;
            default: return inner;
        }
    }, html);
}

export default function renderVersion(node: Node | null | undefined): string {
    if (!node) return "";
    if (node.type === "text") return withMarks(escape(node.text ?? ""), node.marks);

    const inner = (node.content ?? []).map(renderVersion).join("");
    const align = node.attrs?.textAlign ? ` style="text-align:${escape(node.attrs.textAlign)}"` : "";

    switch (node.type) {
        case "doc": return inner;
        case "paragraph": return `<p${align}>${inner || "<br>"}</p>`;
        case "heading": return `<h${node.attrs?.level ?? 1}${align}>${inner}</h${node.attrs?.level ?? 1}>`;
        case "bulletList": return `<ul>${inner}</ul>`;
        case "orderedList": return `<ol>${inner}</ol>`;
        case "listItem": return `<li>${inner}</li>`;
        case "blockquote": return `<blockquote>${inner}</blockquote>`;
        case "codeBlock": return `<pre><code>${inner}</code></pre>`;
        case "horizontalRule": return "<hr>";
        case "hardBreak": return "<br>";
        case "table": return `<table><tbody>${inner}</tbody></table>`;
        case "tableRow": return `<tr>${inner}</tr>`;
        case "tableHeader": return `<th>${inner}</th>`;
        case "tableCell": return `<td>${inner}</td>`;
        // THE FOOTER AND ITS FIELDS. A version is a body and the footer is part of it, so a
        // version preview that dropped it would show a document the restore would not produce.
        // A field renders the value it holds NOW, because that is what a restore would produce --
        // a field does not record what the revision used to be.
        case "docHeader": return `<div class="parkour-header">${inner}</div>`;
        case "docFooter": return `<div class="parkour-footer">${inner}</div>`;
        case "docField":
            return `<span class="parkour-field">${escape(fieldText(node.attrs?.field ?? "objectId"))}</span>`;
        // AN UNKNOWN NODE RENDERS ITS CHILDREN rather than vanishing. A body written under a later
        // schema must still be readable here -- losing a paragraph because its wrapper is new would
        // make an old version look shorter than it was.
        default: return inner;
    }
}
